/**
 * Offline-first storage for Surveys.
 *
 * Three IndexedDB stores in the `qm-surveys-v1` database:
 *   - surveys   (keyPath: client_id) — full survey doc (answers, photo refs…)
 *   - photos    (keyPath: id)        — Blob photos keyed by a local id
 *   - ops       (autoIncrement)      — sync queue: { type, payload, attempts }
 *
 * Survey state machine:
 *   - synced   — has server id, no queued ops
 *   - pending  — queued ops exist
 *   - error    — last drain attempt failed
 *
 * On the wire surveys carry a `client_id` so re-sent creates are deduped by
 * the server (see `routes/surveys.py::create_survey`).
 */
import { api } from "./apiClient";

const DB_NAME = "qm-surveys-v1";
const DB_VERSION = 1;

let _dbPromise = null;
function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("surveys")) {
        db.createObjectStore("surveys", { keyPath: "client_id" });
      }
      if (!db.objectStoreNames.contains("photos")) {
        db.createObjectStore("photos", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("ops")) {
        db.createObjectStore("ops", { autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function tx(storeNames, mode = "readonly") {
  return openDb().then((db) => db.transaction(storeNames, mode));
}

function wrap(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------- Surveys ----------
export async function putLocalSurvey(survey) {
  const t = await tx(["surveys"], "readwrite");
  await wrap(t.objectStore("surveys").put({ ...survey, updated_at_local: new Date().toISOString() }));
  return survey;
}

export async function getLocalSurvey(clientId) {
  const t = await tx(["surveys"]);
  return wrap(t.objectStore("surveys").get(clientId));
}

export async function listLocalSurveys() {
  const t = await tx(["surveys"]);
  return wrap(t.objectStore("surveys").getAll());
}

export async function removeLocalSurvey(clientId) {
  const t = await tx(["surveys"], "readwrite");
  return wrap(t.objectStore("surveys").delete(clientId));
}

// ---------- Photos ----------
export async function putLocalPhoto(id, blob, meta = {}) {
  const t = await tx(["photos"], "readwrite");
  await wrap(t.objectStore("photos").put({ id, blob, ...meta, added_at: new Date().toISOString() }));
}

export async function getLocalPhoto(id) {
  const t = await tx(["photos"]);
  return wrap(t.objectStore("photos").get(id));
}

export async function removeLocalPhoto(id) {
  const t = await tx(["photos"], "readwrite");
  return wrap(t.objectStore("photos").delete(id));
}

// ---------- Queue ----------
export async function enqueueOp(op) {
  const t = await tx(["ops"], "readwrite");
  await wrap(t.objectStore("ops").add({ ...op, attempts: 0, queued_at: new Date().toISOString() }));
  scheduleSync();
}

export async function listOps() {
  const t = await tx(["ops"]);
  const store = t.objectStore("ops");
  return new Promise((resolve, reject) => {
    const out = [];
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cur = e.target.result;
      if (cur) {
        out.push({ _key: cur.key, ...cur.value });
        cur.continue();
      } else {
        resolve(out);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

async function deleteOp(key) {
  const t = await tx(["ops"], "readwrite");
  return wrap(t.objectStore("ops").delete(key));
}

async function updateOp(key, patch) {
  const t = await tx(["ops"], "readwrite");
  const store = t.objectStore("ops");
  const existing = await wrap(store.get(key));
  if (existing) {
    await wrap(store.put({ ...existing, ...patch }, key));
  }
}

// ---------- Sync state ----------
const listeners = new Set();
let syncing = false;

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  listeners.forEach((fn) => { try { fn(); } catch (_) { /* ignore */ } });
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

let _syncTimer = null;
export function scheduleSync(delay = 250) {
  if (!isOnline()) return;
  if (_syncTimer) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(() => { _syncTimer = null; syncNow(); }, delay);
}

/**
 * Drain the queue. Returns counts.
 * Ops are processed in insertion order; a failing op blocks later ops on the
 * same survey by being requeued (with `attempts++`) so retry-with-backoff is
 * sane.
 */
export async function syncNow() {
  if (syncing) return { skipped: true };
  if (!isOnline()) return { offline: true };
  syncing = true;
  let ok = 0, failed = 0;
  try {
    const ops = await listOps();
    for (const op of ops) {
      try {
        await runOp(op);
        await deleteOp(op._key);
        ok++;
      } catch (e) {
        failed++;
        await updateOp(op._key, { attempts: (op.attempts || 0) + 1, last_error: String(e?.message || e) });
        // Stop draining on first failure for the same survey to preserve order
        break;
      }
    }
  } finally {
    syncing = false;
    notify();
  }
  return { ok, failed };
}

async function runOp(op) {
  if (op.type === "create_survey") {
    const local = await getLocalSurvey(op.client_id);
    if (!local) return;
    if (local.server_id) return; // already created
    const body = { ...stripLocalFields(local), client_id: op.client_id };
    const { data } = await api.post("/surveys", body);
    await putLocalSurvey({ ...local, server_id: data.id, status: data.status, synced_at: new Date().toISOString() });
  } else if (op.type === "update_survey") {
    const local = await getLocalSurvey(op.client_id);
    if (!local || !local.server_id) {
      // server doesn't know the survey yet — recreate so update has a target
      await enqueueOp({ type: "create_survey", client_id: op.client_id });
      throw new Error("Server id missing — re-queued create first");
    }
    const body = stripLocalFields(local);
    await api.put(`/surveys/${local.server_id}`, body);
    await putLocalSurvey({ ...local, synced_at: new Date().toISOString() });
  } else if (op.type === "upload_photo") {
    const local = await getLocalSurvey(op.client_id);
    if (!local?.server_id) {
      await enqueueOp({ type: "create_survey", client_id: op.client_id });
      throw new Error("Server id missing — re-queued create first");
    }
    const photo = await getLocalPhoto(op.photo_id);
    if (!photo) return;
    const fd = new FormData();
    fd.append("file", photo.blob, photo.filename || `photo-${op.photo_id}.jpg`);
    fd.append("question_id", op.question_id);
    const { data } = await api.post(`/surveys/${local.server_id}/photos`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    // Replace the local placeholder id with the server-side id in the survey's photo_ids
    const photoIds = { ...(local.photo_ids || {}) };
    photoIds[op.question_id] = (photoIds[op.question_id] || []).map((pid) => pid === op.photo_id ? data.id : pid);
    await putLocalSurvey({ ...local, photo_ids: photoIds });
    await removeLocalPhoto(op.photo_id);
    // Trigger an update so the new photo_id is persisted server-side
    await enqueueOp({ type: "update_survey", client_id: op.client_id });
  } else if (op.type === "delete_survey") {
    if (op.server_id) {
      try { await api.delete(`/surveys/${op.server_id}`); } catch (_) { /* already gone */ }
    }
    await removeLocalSurvey(op.client_id);
  }
}

function stripLocalFields(local) {
  // Strip purely-local bookkeeping before sending to server
  // eslint-disable-next-line no-unused-vars
  const { server_id, synced_at, updated_at_local, _sync_error, ...rest } = local;
  return rest;
}

// ---------- Per-survey sync status (derived) ----------
export async function statusFor(clientId) {
  const ops = await listOps();
  const mine = ops.filter((o) => o.client_id === clientId);
  if (mine.length === 0) return "synced";
  if (mine.some((o) => (o.attempts || 0) > 0 && o.last_error)) return "error";
  return "pending";
}

export async function allStatuses() {
  const ops = await listOps();
  const map = {};
  for (const o of ops) {
    if (!o.client_id) continue;
    const cur = map[o.client_id] || "pending";
    if ((o.attempts || 0) > 0 && o.last_error) {
      map[o.client_id] = "error";
    } else if (cur !== "error") {
      map[o.client_id] = "pending";
    }
  }
  return map;
}

// ---------- Browser online/offline wiring ----------
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { scheduleSync(50); notify(); });
  window.addEventListener("offline", () => { notify(); });
  // Best-effort initial drain
  if (isOnline()) scheduleSync(1500);
}

export function clientId() {
  // RFC4122-ish — good enough for client-side dedupe keys
  try {
    if (crypto && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* ignore */ }
  return "cid-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}
